import { Button, Flex, Icon, Text, useMediaQuery } from '@chakra-ui/react'
import { useAtom, useStore } from 'jotai'
import { ExternalLink, Settings, X } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import styled from 'styled-components'
import { DesktopAppLaunchConfirmModal } from '~/components/DesktopAppLaunchConfirmModal'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { Tooltip } from '~/components/ui/tooltip'
import {
  usePartnerDesktopAppDownloadUrl,
  usePartnerDesktopAppName,
} from '~/hooks/useCurrentPartner'
import { useDesktopAppLaunchConfirm } from '~/hooks/useDesktopAppLaunchConfirm'
import { BannerIconButton } from '~/pages/Home/Banners/BannerControls'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'
import { desktopAppDetectionBannerStateAtom } from '~/pages/Home/Banners/utils/desktopAppDetectionBannerLogicState'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'
import { buildDeepLinkForPath } from '~/utils/desktopDeepLink'
import { getDesktopAppPlatform } from '~/utils/deviceType'
import { openInNewTab } from '~/utils/functions'

import {
  handleDismissAction,
  handleDownloadNavigation,
  handleIgnoreAction,
  shouldShowDesktopAppBanner,
} from './utils/desktopAppDetectionBannerLogic'

interface WrapperProps {
  $show: boolean
  $isHydrated: boolean
}

const Wrapper = styled.div<WrapperProps>`
  height: ${({ $show }) => ($show ? '56' : '0')}px;
  padding: 0 16px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  background-color: var(--chakra-colors-green-solid);
  align-items: center;
  overflow: hidden;
  transition:
    height 200ms ease,
    opacity 200ms ease;
  opacity: ${({ $isHydrated }) => ($isHydrated ? '1' : '0')};
`
const TextWrapper = styled.div`
  display: flex;
  align-items: center;
`

const DesktopAppDetectionBanner = ({ onShowCallback }: BannerProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const store = useStore()
  const [isSmall] = useMediaQuery(['(width < 860px)'])
  const appName = usePartnerDesktopAppName()
  const downloadUrl = usePartnerDesktopAppDownloadUrl()

  const [bannerState] = useAtom(desktopAppDetectionBannerStateAtom)
  const [showBanner, setShowBanner] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  useEffect(() => {
    if (!isHydrated) return

    if (isElectron || !getDesktopAppPlatform()) {
      setShowBanner(false)
      onShowCallback(false)
      return
    }

    const shouldShow = shouldShowDesktopAppBanner(bannerState)

    setShowBanner(shouldShow)
    onShowCallback(shouldShow)
  }, [bannerState, onShowCallback, isHydrated])

  const handleIgnoreBanner = useCallback(() => {
    setShowBanner(false)
    onShowCallback(false)
    handleIgnoreAction(store)
  }, [store, onShowCallback])

  const handleDismissBanner = useCallback(() => {
    setShowBanner(false)
    onShowCallback(false)
    handleDismissAction(store)
  }, [store, onShowCallback])

  const launchConfirm = useDesktopAppLaunchConfirm()
  const { launch: launchDesktopApp, close: closeLaunchConfirm } = launchConfirm

  const handleLaunchApp = useCallback(() => {
    launchDesktopApp(
      buildDeepLinkForPath(
        `${location.pathname}${location.search}${location.hash}`,
      ),
    )
  }, [launchDesktopApp, location.pathname, location.search, location.hash])

  const handleLaunchConfirmed = useCallback(() => {
    closeLaunchConfirm()
    handleDismissBanner()
  }, [closeLaunchConfirm, handleDismissBanner])

  const handleGotoDownload = useCallback(() => {
    openInNewTab(downloadUrl)
    setShowBanner(false)
    onShowCallback(false)
    handleDownloadNavigation(store)
  }, [downloadUrl, store, onShowCallback])

  const handleOpenSettings = useCallback(() => {
    navigate(ApplicationRoutes.SETTINGS)
  }, [navigate])

  return (
    <Wrapper
      $show={showBanner}
      $isHydrated={isHydrated}
      data-testid="desktop-app-banner"
    >
      <TextWrapper>
        {!isSmall && (
          <Text color="var(--chakra-colors-brand-contrast)">
            {t('desktopAppBanner.tryDesktopApp', {
              appName,
              defaultValue:
                'Try the {{appName}} desktop app for the best experience',
            })}
          </Text>
        )}
        {isSmall && (
          <Text color="var(--chakra-colors-brand-contrast)">
            {t('desktopAppBanner.desktopAppName', {
              appName,
              defaultValue: '{{appName}} Desktop',
            })}
          </Text>
        )}
        <Tooltip content={t('desktopAppBanner.launchAppTooltip')}>
          <Button
            onClick={handleLaunchApp}
            color="fg.default"
            bg="bg.panel"
            borderRadius="full"
            _hover={{ bg: 'green.subtle' }}
            size="sm"
            style={{ marginLeft: '.75rem', minWidth: 'auto' }}
          >
            {t('desktopAppBanner.launchApp')}
          </Button>
        </Tooltip>
        <Tooltip content={t('desktopAppBanner.downloadAppTooltip')}>
          <Button
            onClick={handleGotoDownload}
            color="fg.default"
            bg="bg.panel"
            borderRadius="full"
            _hover={{ bg: 'green.subtle' }}
            size="sm"
            style={{ marginLeft: '.5rem', minWidth: 'auto' }}
          >
            {t('desktopAppBanner.download')}
            <Icon fontSize=".8rem" ml={2}>
              <ExternalLink size="100%" />
            </Icon>
          </Button>
        </Tooltip>
      </TextWrapper>
      <Flex flexShrink={1} justifyContent="flex-end" alignItems={'center'}>
        <Tooltip
          content={t('desktopAppBanner.settings', 'Settings')}
          positioning={{ placement: 'bottom-start' }}
        >
          <BannerIconButton
            aria-label={t('desktopAppBanner.settings', 'Settings')}
            marginLeft="1rem"
            onClick={handleOpenSettings}
          >
            <Icon
              style={{
                color: 'var(--chakra-colors-brand-contrast)',
              }}
              width="20px"
              height="20px"
              aria-hidden
            >
              <Settings size="100%" />
            </Icon>
          </BannerIconButton>
        </Tooltip>
        <MangoButton
          style={{ marginLeft: '.5rem' }}
          size={Size.sm}
          variant={MangoButtonVariant.Default_200}
          onClick={handleIgnoreBanner}
        >
          {t('desktopAppBanner.dontAskAgain')}
        </MangoButton>
        <Tooltip
          content={t('desktopAppBanner.remindMeLater', 'Remind me later')}
          positioning={{ placement: 'bottom-start' }}
        >
          <BannerIconButton
            aria-label={t('desktopAppBanner.remindMeLater', 'Remind me later')}
            marginLeft="1rem"
            onClick={handleDismissBanner}
          >
            <Icon boxSize="16px" color="brand.contrast" aria-hidden>
              <X size="100%" />
            </Icon>
          </BannerIconButton>
        </Tooltip>
      </Flex>
      <DesktopAppLaunchConfirmModal
        launchConfirm={launchConfirm}
        onConfirmed={handleLaunchConfirmed}
        title={t(
          'desktopAppBanner.launchConfirmTitle',
          'Open in the desktop app',
        )}
        confirmHint={t(
          'desktopAppBanner.alwaysOpenHint',
          'To always open links in the desktop app, change this in Settings.',
        )}
      />
    </Wrapper>
  )
}

export default DesktopAppDetectionBanner
